// no longer in use, do not migrate (already part of other files in src_new)
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Content as BaseContent } from '@xingternal/winery-ui/lib/Content';
import { Box, Flex } from '@xingternal/winery-ui/lib/UI';
import { MEDIA_SIZES } from '@helpers/styles';

// This height is the addition of the Xing general header
// and the XTS internal subheader. It can be changed by properties
const DEFAULT_HEADER_HEIGHT = 144;

/**
 * Required to have scroll in the content area only
 */
const ScrollWrapper = styled(Box).attrs({
  id: 'scroll-wrapper',
})`
  height: calc(
    100vh -
      ${({ headerHeight }) =>
        typeof headerHeight !== 'undefined'
          ? headerHeight
          : DEFAULT_HEADER_HEIGHT}px
  );
  overflow: ${({ scroll }) => (scroll ? 'auto' : 'hidden')};
  width: 100%;
`;

/**
 *
 * Old header (temporary keeping it for backward compatibility)
 */
const HeaderWrapper = styled(Box).attrs({
  zIndex: 'fixedLevel',
  width: '100%',
  boxShadow: 2,
})``;

const Header = ({ className, children }) => (
  <HeaderWrapper className={className}>{children}</HeaderWrapper>
);

const ResponsiveBaseContent = styled(BaseContent)`
  padding: ${themeGet('space.2')};

  ${MEDIA_SIZES.MEDIUM_AND_MORE} {
    padding: ${themeGet('space.5')};
  }
`;

/**
 *
 * Content consumes Winery Content and wraps it with our scroll
 */
const Content = ({
  children,
  headerHeight,
  paddingTop = 5,
  paddingBottom = 56,
  variant = CONTENT_VARIANTS_ENUM.default,
  dataTestid,
  scroll = true,
}) => (
  <ScrollWrapper
    headerHeight={headerHeight}
    paddingTop={paddingTop}
    paddingBottom={paddingBottom}
    scroll={scroll}
  >
    <ResponsiveBaseContent variant={variant} data-testid={dataTestid}>
      {children}
    </ResponsiveBaseContent>
  </ScrollWrapper>
);

const CONTENT_VARIANTS_ENUM = {
  wide: 'wide',
  fluid: 'fluid',
  default: 'default',
};

Content.propTypes = {
  children: PropTypes.node.isRequired,
  headerHeight: PropTypes.number,
  paddingTop: PropTypes.number,
  variant: PropTypes.oneOf(Object.keys(CONTENT_VARIANTS_ENUM)),
  dataTestid: PropTypes.string,
  scroll: PropTypes.bool,
};

/**
 * Use it for pages where there are no headers at all
 * and you need the scroll bar for the whole y-axis of the page
 */
const FullPageContent = ({
  children,
  variant = CONTENT_VARIANTS_ENUM.default,
  dataTestid,
  ...rest
}) => (
  <BaseContent
    pt={56}
    pb={56}
    variant={variant}
    data-testid={dataTestid}
    {...rest}
  >
    {children}
  </BaseContent>
);

FullPageContent.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(Object.keys(CONTENT_VARIANTS_ENUM)),
  dataTestid: PropTypes.string,
};

/**
 *
 * New Header fixes all what we pass as a children
 * is fixed prop is true. So we can add and remove elements
 * from the header without worry about keeping them fixed.
 * You still need to tell it the total height of the final header.
 * (but it has a default!)
 */
const getHeaderPosition = (props) =>
  props.fixed ? 'position: fixed' : 'position: relative';

const HeaderContainer = styled(Box)`
  ${getHeaderPosition};
  width: 100%;
  z-index: ${themeGet('zIndices.sidebarLevel')};
`;

const HeaderNew = forwardRef(({ children, fixed, height }, ref) => (
  <Box height={height}>
    <HeaderContainer ref={ref} fixed={fixed}>
      {children}
    </HeaderContainer>
  </Box>
));

HeaderNew.propTypes = {
  children: PropTypes.node.isRequired,
  fixed: PropTypes.bool,
  height: PropTypes.number,
};

HeaderNew.defaultProps = {
  height: DEFAULT_HEADER_HEIGHT,
  fixed: true,
};

/**
 * 
 * Page component 
 * Usage:
 * return (
    <Page>
      <Page.Header fixed>
        <YourHeaderComponents />
      </Page.Header>
      <Page.Content wide>
        <YourContentComponents />
      </Page.Content>
    </Page>
  );
 */
const Page = styled(Flex).attrs({
  height: '100%',
  flexDirection: 'column',
})``;

Page.Header = HeaderNew;
Page.Content = Content;
Page.FullPageContent = FullPageContent;

Page.propTypes = {
  children: PropTypes.node.isRequired,
};

export { Page, Header, CONTENT_VARIANTS_ENUM };
